// Extract the set of sample names referenced inside `s("…")` calls
// in a Strudel patch. Used to pre-resolve unknown names against the
// cloud library before the patch evaluates — without this, missing
// samples spam the harness's window.error listener (one per voice
// trigger per cycle) and the user gets a status-bar loop.
//
// The mini-notation grammar is small but irregular. We deliberately
// stay loose: the goal is "find every plausible identifier", not
// "perfectly parse mini-notation". Over-extraction is harmless —
// `getSound("foo:bar")` returns undefined and the resolver just won't
// find a cloud row by that name. Under-extraction is the failure
// mode we care about.

// Captures the string body of every  s("…")  or  s('…')  call,
// bare or method-form. The lookbehind blocks neighbour-identifier
// matches like `bs("…")` and `xs("…")`, but deliberately permits a
// leading `.` so the canonical `note("c4 e4").s("bell")` idiom is
// also captured — that's where most pitched-sample patches live, and
// missing them produces "sound not found" spam at trigger time.
const S_CALL_RE = /(?<![A-Za-z0-9_$])s\s*\(\s*(["'])([^"']*)\1\s*\)/g;

// Mini-notation control / structural chars. Replaced with whitespace
// so token splitting catches names embedded in `[bd cp]`, `<bd hh>`,
// etc. The bar pipe `|` (random choice) and comma (chord separator)
// are tokenizers too.
const STRUCTURAL_CHARS = /[<>[\](){}|,]/g;

// Suffixes that follow a sample name in mini-notation. Stripping
// everything from the first match onward leaves the bare name.
//   bd*4    replicate
//   bd!2    multiplier
//   bd:0    sample index
//   bd?     stochastic skip
//   bd@2    duration weight
//   bd.foo  property access (rare)
const SUFFIX_RE = /[:*!?@.].*$/;

// Reserved tokens that aren't sample names. `~` is silence; `_` is
// elongation; bare `-` is also rest in some dialects.
const RESERVED = new Set(['~', '_', '-']);

// A valid sample identifier: at least one letter, otherwise letters /
// digits / underscore / dash in any order. Names like `808-kick` and
// `303-bass` are common, so we allow leading digits. The "must contain
// a letter" rule rejects pure-numeric mini-notation tokens (`0`, `1`,
// `123`) that would otherwise pass.
const NAME_RE = /^[a-zA-Z0-9_-]*[a-zA-Z][a-zA-Z0-9_-]*$/;

/** Return the unique set of sample names referenced via `s(…)` in the
 *  given patch code. Order is insertion-order (first appearance). */
export function extractSampleNames(code: string): string[] {
  const out = new Set<string>();
  let m: RegExpExecArray | null;
  // RegExp is stateful via `g`; reset lastIndex each call to make this
  // safely re-entrant.
  S_CALL_RE.lastIndex = 0;
  while ((m = S_CALL_RE.exec(code)) !== null) {
    const body = m[2]!;
    for (const tok of tokenizeBody(body)) {
      out.add(tok);
    }
  }
  return Array.from(out);
}

function tokenizeBody(body: string): string[] {
  const out: string[] = [];
  const cleaned = body.replace(STRUCTURAL_CHARS, ' ');
  for (const raw of cleaned.split(/\s+/)) {
    if (!raw) continue;
    if (RESERVED.has(raw)) continue;
    const name = raw.replace(SUFFIX_RE, '');
    if (!name) continue;
    if (NAME_RE.test(name)) out.push(name);
  }
  return out;
}
